import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const NCR_API_URL = `${BASE_API_URL}/Ncr`;

// ========== Data Models ==========

export interface NcrAction {
  id?: number;
  ncrStageId?: number;
  actionNumber?: number;
  targetDate?: string;
  closedDate?: string;
  completeDate?: string;
  actionDetails?: string;
  actionResponse?: string;
  ownerId?: number;
  ownerName?: string;
  status?: string; // open, in-progress, close
  createdAt?: string;
  updatedAt?: string;
  attachments?: NcrAttachment[];
}

export interface NcrAttachment {
  id?: number;
  ncrActionId?: number;
  fileName: string;
  filePath: string;
  uploadedBy?: number;
  uploadedAt?: string;
}

export interface NcrStageProperty {
  id?: number;
  ncrStageId?: number;
  templatePropertyId?: number;
  propertyName?: string;
  value?: string;
  attachments?: NcrAttachment[];
}

export interface NcrStage {
  id?: number;
  ncrId?: number;
  templateStageId?: number;
  parentStageId?: number;
  parentTemplateStageId?: number;
  workflowLevel?: number;
  stageName?: string;
  status?: string; // open, in-progress, close
  ownerId?: number;
  ownerName?: string;
  targetDate?: string;
  completeDate?: string;
  detail?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: number;
  isLocked?: boolean; // 根据依赖关系锁定
  createdAt?: string;
  updatedAt?: string;
  actions: NcrAction[];
  properties: NcrStageProperty[];
}

export interface NcrStageDependency {
  id?: number;
  ncrId?: number;
  fromStageId?: number;
  toStageId?: number;
  dependencyType?: string; // 'sequential', 'parallel', etc.
  createdAt?: string;
}

export interface Ncr {
  id?: number;
  ncrNumber: string;
  description?: string;
  raisedDate?: string;
  targetDate?: string;
  completeDate?: string;
  severity?: string;
  templateId: number;
  templateName?: string;
  ownerId?: number;
  ownerName?: string;
  status?: string; // open, in-progress, close
  createdAt?: string;
  updatedAt?: string;
  stages: NcrStage[];
  stageDependencies?: NcrStageDependency[];
  properties?: NcrStageProperty[];
}

const normalizeStatus = (status?: string) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'draft' || normalized === 'open' || normalized === 'pending') {
    return 'open';
  }
  if (normalized === 'completed' || normalized === 'closed' || normalized === 'close') {
    return 'close';
  }
  if (normalized === 'in progress') {
    return 'in-progress';
  }
  if (normalized === 'in-progress') {
    return 'in-progress';
  }
  return 'open';
};

// ========== Auth Headers ==========

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
  };
};

// ========== Normalization Functions ==========

const normalizeAction = (action: any): NcrAction => ({
  id: action?.id ?? action?.Id,
  ncrStageId: action?.ncrStageId ?? action?.NcrStageId,
  actionNumber: action?.actionNumber ?? action?.ActionNumber ?? action?.actionNo ?? action?.ActionNo,
  targetDate: action?.targetDate ?? action?.TargetDate,
  closedDate: action?.closedDate ?? action?.ClosedDate,
  actionDetails: action?.actionDetails ?? action?.ActionDetails ?? action?.requestDesc ?? action?.RequestDesc ?? action?.requestDescription ?? action?.RequestDescription ?? '',
  actionResponse: action?.actionResponse ?? action?.ActionResponse ?? action?.responseDesc ?? action?.ResponseDesc ?? action?.responseDescription ?? action?.ResponseDescription ?? '',
  ownerId: action?.ownerId ?? action?.OwnerId,
  ownerName: action?.ownerName ?? action?.OwnerName ?? '',
  status: normalizeStatus(action?.status ?? action?.Status),
  createdAt: action?.createdAt ?? action?.CreatedAt,
  updatedAt: action?.updatedAt ?? action?.UpdatedAt,
  attachments: (action?.attachments ?? action?.Attachments ?? []).map(normalizeAttachment),
});

const normalizeAttachment = (attachment: any): NcrAttachment => ({
  id: attachment?.id ?? attachment?.Id,
  ncrActionId: attachment?.ncrActionId ?? attachment?.NcrActionId,
  fileName: attachment?.fileName ?? attachment?.FileName ?? '',
  filePath: attachment?.filePath ?? attachment?.FilePath ?? '',
  uploadedBy: attachment?.uploadedBy ?? attachment?.UploadedBy,
  uploadedAt: attachment?.uploadedAt ?? attachment?.UploadedAt,
});

const normalizeStageProperty = (prop: any): NcrStageProperty => ({
  id: prop?.id ?? prop?.Id,
  ncrStageId: prop?.ncrStageId ?? prop?.NcrStageId,
  templatePropertyId: prop?.templatePropertyId ?? prop?.TemplatePropertyId,
  propertyName: prop?.propertyName ?? prop?.PropertyName ?? '',
  value: prop?.value ?? prop?.Value ?? '',
  attachments: (prop?.attachments ?? prop?.Attachments ?? []).map(normalizeAttachment),
});

const normalizeStage = (stage: any): NcrStage => ({
  id: stage?.id ?? stage?.Id,
  ncrId: stage?.ncrId ?? stage?.NcrId,
  templateStageId: stage?.templateStageId ?? stage?.TemplateStageId,
  parentStageId: stage?.parentStageId ?? stage?.ParentStageId,
  parentTemplateStageId: stage?.parentTemplateStageId ?? stage?.ParentTemplateStageId,
  workflowLevel: stage?.workflowLevel ?? stage?.WorkflowLevel ?? 0,
  stageName: stage?.stageName ?? stage?.StageName ?? stage?.name ?? stage?.Name ?? '',
  status: normalizeStatus(stage?.status ?? stage?.Status),
  ownerId: stage?.ownerId ?? stage?.OwnerId,
  ownerName: stage?.ownerName ?? stage?.OwnerName ?? '',
  targetDate: stage?.targetDate ?? stage?.TargetDate,
  completeDate: stage?.completeDate ?? stage?.CompleteDate,
  detail: stage?.detail ?? stage?.Detail ?? stage?.description ?? stage?.Description ?? '',
  startDate: stage?.startDate ?? stage?.StartDate,
  endDate: stage?.endDate ?? stage?.EndDate,
  sortOrder: stage?.sortOrder ?? stage?.SortOrder ?? 0,
  isLocked: Boolean(stage?.isLocked ?? stage?.IsLocked ?? false),
  createdAt: stage?.createdAt ?? stage?.CreatedAt,
  updatedAt: stage?.updatedAt ?? stage?.UpdatedAt,
  actions: (stage?.actions ?? stage?.Actions ?? []).map(normalizeAction),
  properties: (stage?.properties ?? stage?.Properties ?? []).map(normalizeStageProperty),
});

const normalizeStageDependency = (dep: any): NcrStageDependency => ({
  id: dep?.id ?? dep?.Id,
  ncrId: dep?.ncrId ?? dep?.NcrId,
  fromStageId: dep?.fromStageId ?? dep?.FromStageId,
  toStageId: dep?.toStageId ?? dep?.ToStageId,
  dependencyType: dep?.dependencyType ?? dep?.DependencyType ?? 'sequential',
  createdAt: dep?.createdAt ?? dep?.CreatedAt,
});

const normalizeNcr = (ncr: any): Ncr => ({
  id: ncr?.id ?? ncr?.Id,
  ncrNumber: ncr?.ncrNumber ?? ncr?.NcrNumber ?? '',
  description: ncr?.description ?? ncr?.Description ?? '',
  raisedDate: ncr?.raisedDate ?? ncr?.RaisedDate ?? ncr?.raisedAt ?? ncr?.RaisedAt,
  targetDate: ncr?.targetDate ?? ncr?.TargetDate,
  completeDate: ncr?.completeDate ?? ncr?.CompleteDate,
  severity: ncr?.severity ?? ncr?.Severity ?? 'Low Risk',
  templateId: ncr?.templateId ?? ncr?.TemplateId,
  templateName: ncr?.templateName ?? ncr?.TemplateName ?? ncr?.templateInstanceName ?? ncr?.TemplateInstanceName ?? '',
  ownerId: ncr?.ownerId ?? ncr?.OwnerId,
  ownerName: ncr?.ownerName ?? ncr?.OwnerName ?? ncr?.ownerUsername ?? ncr?.OwnerUsername ?? '',
  status: normalizeStatus(ncr?.status ?? ncr?.Status),
  createdAt: ncr?.createdAt ?? ncr?.CreatedAt,
  updatedAt: ncr?.updatedAt ?? ncr?.UpdatedAt,
  stages: (ncr?.stages ?? ncr?.Stages ?? []).map(normalizeStage),
  stageDependencies: (ncr?.stageDependencies ?? ncr?.StageDependencies ?? []).map(normalizeStageDependency),
  properties: (ncr?.properties ?? ncr?.Properties ?? []).map(normalizeStageProperty),
});

// ========== API Functions ==========

/**
 * 获取所有 NCR 列表
 */
export const getAllNcrs = async (
  filters?: { templateId?: number; status?: string; ownerId?: number }
): Promise<Ncr[]> => {
  try {
    const response = await axios.get(NCR_API_URL, {
      headers: getAuthHeaders(),
      params: filters,
    });
    const data = response.data;
    const items = Array.isArray(data) ? data : data?.items ?? [];
    return items.map(normalizeNcr);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to fetch NCRs');
  }
};

/**
 * 根据 ID 获取 NCR 详情
 */
export const getNcrById = async (id: number): Promise<Ncr> => {
  try {
    const response = await axios.get(`${NCR_API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
    return normalizeNcr(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to fetch NCR');
  }
};

/**
 * 创建新 NCR
 */
export const createNcr = async (ncr: Partial<Ncr>): Promise<Ncr> => {
  try {
    const response = await axios.post(NCR_API_URL, ncr, {
      headers: getAuthHeaders(),
    });
    return normalizeNcr(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to create NCR');
  }
};

/**
 * 更新 NCR
 */
export const updateNcr = async (id: number, ncr: Partial<Ncr>): Promise<Ncr> => {
  try {
    const response = await axios.put(`${NCR_API_URL}/${id}`, ncr, {
      headers: getAuthHeaders(),
    });
    return normalizeNcr(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to update NCR');
  }
};

/**
 * 删除 NCR
 */
export const deleteNcr = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${NCR_API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to delete NCR');
  }
};

// ========== NCR Stage Operations ==========

/**
 * 为 NCR 创建 Stage（从 Template 复制）
 */
export const createNcrStage = async (ncrId: number, stage: Partial<NcrStage>): Promise<NcrStage> => {
  try {
    const response = await axios.post(`${NCR_API_URL}/${ncrId}/stages`, stage, {
      headers: getAuthHeaders(),
    });
    return normalizeStage(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to create stage');
  }
};

/**
 * 更新 NCR Stage
 */
export const updateNcrStage = async (ncrId: number, stageId: number, stage: Partial<NcrStage>): Promise<NcrStage> => {
  try {
    const response = await axios.put(`${NCR_API_URL}/${ncrId}/stages/${stageId}`, stage, {
      headers: getAuthHeaders(),
    });
    return normalizeStage(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to update stage');
  }
};

/**
 * 删除 NCR Stage
 */
export const deleteNcrStage = async (ncrId: number, stageId: number): Promise<void> => {
  try {
    await axios.delete(`${NCR_API_URL}/${ncrId}/stages/${stageId}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to delete stage');
  }
};

// ========== NCR Action Operations ==========

/**
 * 创建 NCR Action
 */
export const createNcrAction = async (ncrId: number, stageId: number, action: Partial<NcrAction>): Promise<NcrAction> => {
  try {
    const response = await axios.post(`${NCR_API_URL}/${ncrId}/stages/${stageId}/actions`, action, {
      headers: getAuthHeaders(),
    });
    return normalizeAction(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to create action');
  }
};

/**
 * 更新 NCR Action
 */
export const updateNcrAction = async (ncrId: number, stageId: number, actionId: number, action: Partial<NcrAction>): Promise<NcrAction> => {
  try {
    const response = await axios.put(`${NCR_API_URL}/${ncrId}/stages/${stageId}/actions/${actionId}`, action, {
      headers: getAuthHeaders(),
    });
    return normalizeAction(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to update action');
  }
};

/**
 * 删除 NCR Action
 */
export const deleteNcrAction = async (ncrId: number, stageId: number, actionId: number): Promise<void> => {
  try {
    await axios.delete(`${NCR_API_URL}/${ncrId}/stages/${stageId}/actions/${actionId}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to delete action');
  }
};

// ========== NCR Stage Dependency Operations ==========

/**
 * 创建 Stage 依赖关系
 */
export const createStageDependency = async (ncrId: number, dependency: Partial<NcrStageDependency>): Promise<NcrStageDependency> => {
  try {
    const response = await axios.post(`${NCR_API_URL}/${ncrId}/dependencies`, dependency, {
      headers: getAuthHeaders(),
    });
    return normalizeStageDependency(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to create dependency');
  }
};

/**
 * 删除 Stage 依赖关系
 */
export const deleteStageDependency = async (ncrId: number, dependencyId: number): Promise<void> => {
  try {
    await axios.delete(`${NCR_API_URL}/${ncrId}/dependencies/${dependencyId}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to delete dependency');
  }
};

// ========== File Upload ==========

/**
 * 上传文件到 Action
 */
export const uploadFileToAction = async (ncrId: number, stageId: number, actionId: number, file: File): Promise<NcrAttachment> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${NCR_API_URL}/${ncrId}/stages/${stageId}/actions/${actionId}/attachments`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return normalizeAttachment(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to upload file');
  }
};

/**
 * 删除附件
 */
export const deleteAttachment = async (attachmentId: number): Promise<void> => {
  try {
    await axios.delete(`${BASE_API_URL}/attachments/${attachmentId}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.message ?? error.message ?? 'Failed to delete attachment');
  }
};
